import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TechRows } from "@/components/tech/TechRows";
import { SignaturePad } from "@/components/tech/SignaturePad";

export const TITLES = { bast: "Berita Acara Serah Terima", survey: "Technical Survey Report", checklist: "Checklist Persiapan Material dan Product untuk Project", mreport: "Maintenance Report" };
const F = ({ label, children }) => <label className="space-y-1"><span className="text-[11px] font-semibold uppercase text-slate-500">{label}</span>{children}</label>;

const BastFields = ({ x, setX }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {[["spk_no", "No. SPK / PO"], ["attachment", "Lampiran"], ["p1_name", "Nama Pihak Pertama (Customer)"], ["p1_title", "Jabatan Pihak Pertama"], ["p2_title", "Jabatan Teknisi"], ["progress", "Progress Pekerjaan (%)"]].map(([k, l]) => (
      <F key={k} label={l}><Input className="bg-white" value={x[k] ?? ""} onChange={(e) => setX({ [k]: e.target.value })} data-testid={`bast-${k}`} /></F>
    ))}
    <div className="sm:col-span-2"><F label="Tanda Tangan Customer (Pihak Pertama)"><SignaturePad value={x.customer_sign} onChange={(p) => setX({ customer_sign: p })} /></F></div>
  </div>
);

export const TechDocForm = ({ type, doc, open, onClose, onSaved }) => {
  const [f, setF] = useState(null);
  const [deals, setDeals] = useState([]);
  useEffect(() => {
    if (!open) return;
    api.get("/tech/deals").then((r) => setDeals(r.data));
    setF(doc || { type, quotation_id: "", project: "", client: "", location: "", date: new Date().toISOString().slice(0, 10), data: type === "bast" ? { p2_title: "Teknisi", progress: 100, attachment: "PO, LIST IP & DOKUMENTASI" } : {}, rows: [] });
  }, [open, doc, type]);
  if (!f) return null;
  const set = (p) => setF((o) => ({ ...o, ...p }));
  const setX = (p) => setF((o) => ({ ...o, data: { ...o.data, ...p } }));
  const pickDeal = (id) => {
    const q = deals.find((d) => d.id === id);
    if (!q) return set({ quotation_id: "" });
    set({ quotation_id: id, client: q.customer_name, project: q.project_title, location: q.customer_address || f.location,
      data: { ...f.data, quote_no: q.display_no, spk_no: f.data.spk_no || q.po_no, p1_name: f.data.p1_name || q.pic_name },
      ...(type === "checklist" ? { rows: q.items.map((it) => ({ name: [it.name, it.description].filter(Boolean).join("\n"), qty: it.qty, unit: it.unit, status: "", note: "" })) } : {}) });
  };
  const save = async () => {
    try {
      const { data } = f.id ? await api.put(`/tech/docs/${f.id}`, f) : await api.post("/tech/docs", f);
      toast.success(`${data.doc_no} disimpan`); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto" aria-describedby={undefined} data-testid="tech-doc-dialog">
        <DialogHeader><DialogTitle className="font-heading">{TITLES[type]} {f.doc_no && <span className="text-sm text-slate-500">• {f.doc_no}</span>}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><F label="Ambil dari Penawaran Deal">
            <select className="h-10 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" value={f.quotation_id} onChange={(e) => pickDeal(e.target.value)} data-testid="tech-deal-select">
              <option value="">— tanpa penawaran —</option>
              {deals.map((d) => <option key={d.id} value={d.id}>{d.display_no} • {d.customer_name} • {d.project_title}</option>)}
            </select></F></div>
          <F label={type === "bast" ? "Proyek / Pihak Pertama" : "Client"}><Input className="bg-white" value={f.client} onChange={(e) => set({ client: e.target.value })} data-testid="tech-client-input" /></F>
          <F label={type === "bast" ? "Pekerjaan" : "Project"}><Input className="bg-white" value={f.project} onChange={(e) => set({ project: e.target.value })} data-testid="tech-project-input" /></F>
          <F label="Lokasi"><Input className="bg-white" value={f.location} onChange={(e) => set({ location: e.target.value })} data-testid="tech-location-input" /></F>
          <F label="Tanggal"><Input type="date" className="bg-white" value={f.date} onChange={(e) => set({ date: e.target.value })} data-testid="tech-date-input" /></F>
          {(type === "survey" || type === "mreport") && <F label="Tim / Prepared By"><Input className="bg-white" placeholder="mis. Awal & Prihanto" value={f.data.team || ""} onChange={(e) => setX({ team: e.target.value })} data-testid="tech-team-input" /></F>}
        </div>
        {type === "bast" ? <BastFields x={f.data} setX={setX} /> : <TechRows type={type} rows={f.rows} setRows={(rows) => set({ rows })} />}
        <Button onClick={save} data-testid="tech-doc-save-btn"><Save className="h-4 w-4 mr-1" />Simpan</Button>
      </DialogContent>
    </Dialog>
  );
};
